import React, { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Home } from './pages/Home';
import PublicMiner from './pages/PublicMiner';
import AOS from 'aos';
import 'aos/dist/aos.css';
import WorkerData from './Components/machinedata/MachineHandle';
import SignInScreen from './Components/auth/SignInScreen';
import SignUpScreen from './Components/auth/SignUpScreen';
import Verify from './Components/auth/Verify';
import ConnectScreen from './Components/auth/ConnectScreen';
import { onAuthStateChanged, onIdTokenChanged, User } from 'firebase/auth';
import { auth, FBuser, signedUser } from './Components/auth/Auth';
import axios from 'axios';
import { CoinProvider } from './context/CoinContext';
import { AccountProvider } from './context/AccountContext';
import { LogoLoader } from './Components/Elements/LogoLoader';
import { LandingLoader } from './Components/Elements/LandingLoader';

export const appContext = React.createContext<any | null>(null);

function AuthenticatedLoginRedirect() {
  useEffect(() => {
    const next = new URLSearchParams(window.location.search).get('next');
    if (!next) {
      return;
    }

    try {
      const target = new URL(next, window.location.origin);
      if (target.origin !== window.location.origin) {
        window.location.replace('/');
        return;
      }
      window.location.replace(target.toString());
    } catch {
      window.location.replace('/');
    }
  }, []);

  const next = new URLSearchParams(window.location.search).get('next');
  if (!next) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <LandingLoader />
    </div>
  );
}

function App() {
  const [authStatus, setAuthStatus] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [authUpdate, setAuthUpdate] = useState<any>(null);
  const [isTotpVerified, setIsTotpVerified] = useState<boolean>(() => {
    return sessionStorage.getItem('isTotpVerified') === 'true';
  });
  const [requiresTotp, setRequiresTotp] = useState<boolean>(false);

  const [authUserName, setAuthUserName] = useState<string>("");
  const [authUserEmail, setAuthUserEmail] = useState<string>("");
  const [authList, setAuthList] = useState<any[]>([]);

  const [darkMode, setDarkMode] = useState<boolean>(() => {
    return localStorage.getItem('theme') === 'dark' ||
      (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
    });
  }, []);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, validateAuthToken);
    const unsubscribeToken = onIdTokenChanged(auth, validateAuthToken);

    return () => {
      unsubscribeAuth();
      unsubscribeToken();
    };
  }, []);

  function validateAuthToken(user: User | null) {
    if (user && !user.emailVerified) {
      // Force sign out if email is not verified
      // We can't easily show a message here since this is top-level, 
      // but SignInScreen will handle the message on explicit login attempt.
      // Here we just ensure they aren't let in via persistent session.
      auth.signOut();
      processFBuser(null);
      return;
    }

    if (user) {
      signedUser(processFBuser);
    } else {
      processFBuser(null);
    }
  }

  async function processFBuser(user: FBuser | null) {
    if (user == null) {
      axios.defaults.headers.common['userId'] = '';
      axios.defaults.headers.common['userFbId'] = '';
      axios.defaults.headers.common['token'] = '';
      setAuthUserName("");
      setAuthUserEmail("");
      setAuthList([]);
      setAuthStatus(false);
      setIsTotpVerified(false);
      setRequiresTotp(false);
      sessionStorage.removeItem('isTotpVerified');
    } else {
      axios.defaults.headers.common['userId'] = user.email;
      axios.defaults.headers.common['userFbId'] = user.id;
      axios.defaults.headers.common['token'] = user.token;

      try {
        const totpCheckResponse = await axios.get('/pool/api/totp/check', {
          params: { email: user.email },
          headers: { Authorization: `Bearer ${user.token}` }
        });

        if (totpCheckResponse.data.requiresTOTP) {
          setRequiresTotp(true);
        } else {
          setRequiresTotp(false);
          setIsTotpVerified(true);
        }
      } catch (error) {
        console.error('TOTP check error:', error);
        // Default to requiring TOTP if check fails? Or allow?
        // For security, maybe stay on safe side.
      }

      setAuthStatus(true);
      setAuthUpdate(new Date().getTime());
      axios.get(`/pool/info`)
        .then(response => {
          setAuthUserName(response.data.userName);
          setAuthUserEmail(response.data.email);
        }).catch(() => { });
    }

    setLoading(false);
  }

  useEffect(() => {
    if (!authStatus || !authUpdate) {
      return;
    }

    axios.get(`/pool/process_feed`)
      .then(response => {
        setAuthList(response.data);
      }).catch(() => { });
  }, [authStatus, authUpdate])


  useEffect(() => {
    axios.defaults.headers.common['userId'] = authUserEmail
  }, [authUserEmail])

  // Add axios interceptor for account switching
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const activeAccount = localStorage.getItem('activeAccount');
      if (activeAccount) {
        config.headers['X-Active-Account'] = activeAccount;
      }
      return config;
    });

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, []);

  const handleTotpVerified = () => {
    setIsTotpVerified(true);
    sessionStorage.setItem('isTotpVerified', 'true');
  };

  return (<appContext.Provider value={{
    authUserName,
    setAuthUserName,
    authUserEmail,
    setAuthUserEmail,
    authList,
    isTotpVerified,
    setIsTotpVerified: handleTotpVerified,
    darkMode,
    setDarkMode
  }}>
    <CoinProvider>
      <AccountProvider>
        <BrowserRouter>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <LandingLoader />
            </div>
          ) : (
            <Routes>
              {/* public whatever the sign-in state: a segproxy miner's dashboard, by BTC address */}
              <Route path="/miner/:address" element={<PublicMiner />} />
              {authStatus === null && (
                <>
                  <Route path="/" element={<Home />} />
                </>
              )}
              {authStatus === false && (
                <>
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<SignInScreen />} />
                  <Route path="/connect" element={<ConnectScreen />} />
                  <Route path="/signup" element={<SignUpScreen />} />
                  <Route path="/verify" element={<Verify />} />
                  <Route path="*" element={<Navigate to="/" />} />
                </>
              )}
              {authStatus === true && (
                <>
                  {requiresTotp && !isTotpVerified ? (
                    <>
                      <Route path="/login" element={<SignInScreen />} />
                      <Route path="/connect" element={<ConnectScreen />} />
                      <Route path="/verify" element={<Navigate to={`/connect?next=${encodeURIComponent(window.location.href)}`} />} />
                      <Route path="*" element={<Navigate to="/connect" />} />
                    </>
                  ) : (
                    <>
                      <Route path="/" element={<WorkerData />} />
                      <Route path="/verify" element={<Verify />} />
                      <Route path="/login" element={<AuthenticatedLoginRedirect />} />
                      <Route path="/connect" element={<ConnectScreen />} />
                      <Route path="/signup" element={<Navigate to="/" />} />
                      <Route path="*" element={<Navigate to="/" />} />
                    </>
                  )}
                </>
              )}
            </Routes>
          )}
        </BrowserRouter>
      </AccountProvider>
    </CoinProvider>
  </appContext.Provider>
  );
}

export default App;
